import React, { useCallback, useMemo, useState } from 'react'
import clsx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { Helmet } from '@dr.pogodin/react-helmet'
import { ServiceSiteType, type Media, type ServicePage as ServicePageData } from 'api/api.types'
import { getActiveServiceGroups } from 'src/store/data.store'
import { setAppointmentServices } from 'src/store/appointment.store'
import { chunk } from 'utils/chunk'
import { getYoutubeEmbedUrl, getYoutubeId, getYoutubePreviewUrl } from 'utils/youtube'
import { ServiceInfo } from 'components/PageElements/ServiceInfo/ServiceInfo'
import { MainMenu } from 'components/PageElements/MainMenu/MainMenu'
import { PageNotFound } from 'components/PageElements/PageNotFound/PageNotFound'
import { ImageLoader } from 'components/Utility/ImageLoader/ImageLoader'
import { Picture } from 'components/Utility/Media/Picture'
import styles from './ServicePage.module.scss'

const THUMBS_PER_ROW = 6
const MAIN_CATEGORIES = [ServiceSiteType.manicure, ServiceSiteType.pedicure, ServiceSiteType.complex]

const CurrentSlide: React.FC<{ media: Media }> = ({ media }) => (
  <div className={styles.currentSlide}>
    {media.type === 'youtube'
      ? <iframe allow="autoplay; encrypted-media" title={media.title} src={getYoutubeEmbedUrl(getYoutubeId(media.youtubeLink))} />
      : <Picture
        image={media.image}
        desktopType={'fullscreen-desktop'}
        mobileType={'fullscreen-mobile'}
        className={styles.currentSlide}
        alt={media.imageAlt}
      />}
    <div className={styles.descriptionImg}>{media.title}</div>
  </div>
)

type ServicePageProps = {
  page: ServicePageData,
}

/** A service with its photo and video gallery; 404 when it is not offered in the selected city */
export const ServicePage: React.FC<ServicePageProps> = ({ page }) => {
  const vars = page.templateVars.pageVars
  const images = useMemo(() => vars.images ?? [], [vars.images])
  const [selectedImageIdx, setSelectedImageIdx] = useState(0)
  const dispatch = useDispatch()
  const serviceGroups = useSelector(getActiveServiceGroups)
  const group = useMemo(() => serviceGroups.find((item) => item.page.id === page.id), [serviceGroups, page.id])

  const handleSelect = useCallback((serviceId: number) => {
    dispatch(setAppointmentServices({ clientIndex: 0, services: [{ id: serviceId, amount: 1 }] }))
  }, [dispatch])

  const shareImage = useMemo(() => {
    const image = images.find((i) => i.type === 'image')
    return image?.image?.find((i) => i.type === 'fullscreen-mobile') ?? null
  }, [images])

  const serviceType = group?.options[0].service.type
  const defaultCategory = serviceType !== undefined && MAIN_CATEGORIES.includes(serviceType)
    ? serviceType
    : ServiceSiteType.complex
  const thumbRows = chunk(images, THUMBS_PER_ROW)
  const selectedImage = images[selectedImageIdx]

  if (!group) {
    return <PageNotFound />
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <meta property={'og:type'} content={'website'} />
        {shareImage && <meta property={'og:image'} content={shareImage.link} />}
        {shareImage && <meta property={'og:image:width'} content={shareImage.resolution.split('x')[0]} />}
        {shareImage && <meta property={'og:image:height'} content={shareImage.resolution.split('x')[1]} />}
      </Helmet>
      <div className={styles.content}>
        <div className={styles.slider}>
          <div className={styles.infoImgBox}>
            {selectedImage && <CurrentSlide media={selectedImage} />}
          </div>
          {thumbRows.map((row, i) => (
            <div key={i} className={styles.thumbnails}>
              {row.map((image, j) => {
                const index = i * THUMBS_PER_ROW + j
                const thumbClassName = clsx([styles.thumb, { [styles.active]: index === selectedImageIdx }])
                if (image.type === 'image') {
                  return (
                    <Picture
                      key={index}
                      image={image.image}
                      desktopType={'preview'}
                      mobileType={'preview'}
                      className={thumbClassName}
                      onClick={() => setSelectedImageIdx(index)}
                      alt={image.imageAlt}
                    />
                  )
                }
                return (
                  <ImageLoader
                    key={index}
                    src={getYoutubePreviewUrl(getYoutubeId(image.youtubeLink))}
                    className={thumbClassName}
                    onClick={() => setSelectedImageIdx(index)}
                  />
                )
              })}
            </div>
          ))}
        </div>
        <div className={styles.service}>
          <div className={styles.info}>
            <ServiceInfo noLink asPageInfo group={group} onSelect={handleSelect} />
          </div>
        </div>
      </div>
      <MainMenu onlyMainCategories defaultCategory={defaultCategory} />
    </div>
  )
}
